/**
 * EmailService - Sends emails via Azure Functions email service endpoint
 * 
 * Sends company invitation emails using Resend email service through Azure Functions proxy.
 * Logs email activity for audit trail.
 * 
 * Boundary: Does not generate email templates; expects pre-rendered HTML
 * External dependencies:
 * - HTTP POST: environment.emailServiceUrl (Azure Functions email endpoint)
 */

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { UserActivityLoggingService } from './user-activity-logging.service';
import { tap } from 'rxjs/operators';

/**
 * Sends email notifications via Azure Functions email service endpoint.
 * Currently supports company invitation emails.
 * 
 * Dependencies:
 * - HttpClient: HTTP POST to email service
 * - UserActivityLoggingService: Logs email sending activity
 * 
 * Side effects:
 * - HTTP POST to environment.emailServiceUrl
 * - Logs email_sent activity via UserActivityLoggingService
 */
@Injectable({
  providedIn: 'root',
})
export class EmailService {
  private http = inject(HttpClient);
  private readonly activityLogging = inject(UserActivityLoggingService);

  private url: string = environment.emailServiceUrl;

  /**
   * Sends company invitation email via Azure Functions email service (Resend provider)
   * 
   * @param userEmail - Recipient email address (must be valid email format)
   * @param htmlData - Pre-rendered HTML email content (complete HTML document)
   * @param fromUserEmail - Sender email address, defaults to 'no_reply@scanninja.ai'
   * @param senderName - Display name for sender, defaults to 'Scan Ninja'
   * @param subject - Email subject line, defaults to 'Scan Ninja Invite'
   * @returns Observable<any> emitting email service API response (Resend API response format)
   * - Observable semantics: Single emission on success, error on HTTP failure
   * - HTTP errors: Network errors, 400/500 from email service propagate as Observable errors
   * - State touched: Logs 'email_sent' activity via UserActivityLoggingService
   * - Idempotency: NOT idempotent - each call sends a new email
   */
  sendCompanyInvite(
    userEmail: string,
    htmlData: string,
    fromUserEmail = 'no_reply@scanninja.ai',
    senderName = 'Scan Ninja',
    subject: string = 'Scan Ninja Invite',
  ): Observable<any> {
    let resendData = null;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    resendData = {
      name: senderName,
      fromUser: fromUserEmail,
      replyTo: 'support@scanninja.ai',
      toUser: userEmail,
      subject: subject,
      htmlEmail: htmlData,
    };

    return this.http.post(this.url, resendData, httpOptions).pipe(
      tap(() => {
        // Log email sending activity
        this.activityLogging.logUserAction('email_sent', {
          type: 'company_invite',
          recipient: userEmail,
          subject,
          sender: senderName,
        });
      })
    );
  }
}
